import { useState } from "react";
import { Card, Avatar, Button, Form, Input, Row, Col, Divider, Tag, Space } from "antd";
import { MailOutlined, LockOutlined, UserOutlined, SafetyOutlined } from "@ant-design/icons";
import styles from "./UserProfilePage.module.scss";
import Main from "../../components/layouts/Main";
import { useUserContext } from "../../contexts/UserContext";
import PageTitle from "../../components/utils/PageTitle";
import UserService from "../../services/UserService";
import Toast from "../../helpers/Toast";
import { catchError } from "../../services/DaoService";

const UserProfilePage = () => {
  const { currentUser: user } = useUserContext();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  if (!user) return null;

  const handlePasswordChange = () => {
    setSaving(true);
    form.validateFields().then(values => {
      UserService.getInstance().changePassword(values)
        .then(response => {
          Toast.success(response.data?.message || response.message);
          form.resetFields();
        })
        .catch(error => {
          const errors = error.response?.data;
          if (errors?.validation && errors?.errors) {
            const fields = Object.entries(errors.errors).map(([name, messages]) => ({
              name,
              errors: messages,
            }));
            form.setFields(fields);
          } else {
            catchError(error);
          }
        })
        .finally(() => setSaving(false));
    }).catch(() => setSaving(false));
  };

  return (
    <Main>
      <PageTitle title="Mon profil" subTitle="Gérez vos informations personnelles" />
      <Row gutter={[16, 16]}>
        {/* Profile card */}
        <Col xs={24} lg={8}>
          <Card className={styles.profileCard}>
            <div className={styles.avatarSection}>
              <Avatar size={80} src={user.avatar} icon={<UserOutlined />} className={styles.avatar} />
              <h2 className={styles.name}>{user.name}</h2>
              {user.is_admin && <Tag color="blue">Administrateur</Tag>}
            </div>
            <Divider style={{ margin: '12px 0' }} />
            <div className={styles.infoList}>
              <div className={styles.infoItem}>
                <MailOutlined className={styles.infoIcon} />
                <span className={styles.infoText}>{user.email}</span>
              </div>
              {user.personnel?.departement?.nom && (
                <div className={styles.infoItem}>
                  <UserOutlined className={styles.infoIcon} />
                  <span className={styles.infoText}>{user.personnel.departement.nom}</span>
                </div>
              )}
            </div>
          </Card>
        </Col>

        {/* Password change */}
        <Col xs={24} lg={16}>
          <Card
            title={<Space><LockOutlined /><span>Changer le mot de passe</span></Space>}
            className={styles.passwordCard}
          >
            <Form form={form} layout="vertical" style={{ maxWidth: 420 }}>
              <Form.Item
                label="Mot de passe actuel"
                name="current_password"
                rules={[{ required: true, message: 'Champ requis' }]}
              >
                <Input.Password prefix={<SafetyOutlined />} placeholder="Mot de passe actuel" />
              </Form.Item>
              <Form.Item
                label="Nouveau mot de passe"
                name="new_password"
                rules={[{ required: true, min: 8, message: 'Minimum 8 caractères' }]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Nouveau mot de passe" />
              </Form.Item>
              <Form.Item
                label="Confirmer le mot de passe"
                name="new_password_confirmation"
                dependencies={['new_password']}
                rules={[
                  { required: true, message: 'Champ requis' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('new_password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject('Les mots de passe ne correspondent pas.');
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Confirmer le mot de passe" />
              </Form.Item>
              <Button type="primary" loading={saving} onClick={handlePasswordChange}>
                Mettre à jour le mot de passe
              </Button>
            </Form>
          </Card>
        </Col>
      </Row>
    </Main>
  );
};

export default UserProfilePage;
